import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const Footer = () => {
  const [activeTab, setActiveTab] = useState("home");
  const navigate = useNavigate();
  const location = useLocation();

  // Updated tabs with paths
  const tabs = [
    {
      id: "home",
      label: "Home",
      icon: "home",
      path: "/dashboard/user",
      exact: true,
    },
    {
      id: "plan",
      label: "Plans",
      icon: "plan",
      path: "/dashboard/user/plans",
      exact: false,
    },
    {
      id: "create",
      label: "Create",
      icon: "plus",
      path: "/create",
      exact: true,
    },
    {
      id: "users",
      label: "Teams",
      icon: "users",
      path: "/dashboard/user/teams",
      exact: false,
    },
    {
      id: "profile",
      label: "Profile",
      icon: "profile",
      path: "/dashboard/user/profile",
      exact: true,
    },
  ];

  // Sync active tab with current route
  useEffect(() => {
    const currentTab = tabs.find((tab) =>
      tab.exact
        ? location.pathname === tab.path
        : location.pathname.startsWith(tab.path)
    );
    if (currentTab && activeTab !== currentTab.id) {
      setActiveTab(currentTab.id);
    }
  }, [location.pathname]);

  // Handle tab click
  const handleTabClick = (tabId, path) => {
    // Optimistically update the active tab before navigation
    setActiveTab(tabId);
    navigate(path);
  };

  // Styles
  const styles = {
    container: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 50,
      display: "flex",
      justifyContent: "center",
      padding: "0 16px",
    },
    footer: {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: "12px 12px 0 0",
      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
      borderTop: "1px solid rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(10px)",
      width: "100%",
      maxWidth: "480px",
      margin: "0 auto",
    },
    tabContainer: {
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
      height: "64px",
      position: "relative",
    },
    tabButton: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      zIndex: 10,
      width: "100%",
      height: "100%",
      background: "transparent",
      border: "none",
      outline: "none",
      cursor: "pointer",
    },
    activeTabHighlight: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      backgroundColor: "rgba(100, 200, 255, 0.2)",
      borderRadius: "8px",
    },
    createButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: "-24px",
      background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
    },
    tabLabel: {
      fontSize: "12px",
      marginTop: "4px",
    },
    icon: {
      width: "24px",
      height: "24px",
    },
  };

  // Icons
  const HomeIcon = ({ active }) => (
    <svg
      style={styles.icon}
      viewBox="0 0 24 24"
      fill={active ? "#3B82F6" : "#6B7280"}
    >
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  );

  const PlanIcon = ({ active }) => (
    <svg
      style={styles.icon}
      viewBox="0 0 24 24"
      fill={active ? "#3B82F6" : "#6B7280"}
    >
      <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
  );

  const UsersIcon = ({ active }) => (
    <svg
      style={styles.icon}
      viewBox="0 0 24 24"
      fill={active ? "#3B82F6" : "#6B7280"}
    >
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );

  const ProfileIcon = ({ active }) => (
    <svg
      style={styles.icon}
      viewBox="0 0 24 24"
      fill={active ? "#3B82F6" : "#6B7280"}
    >
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg style={{ ...styles.icon, fill: "white" }} viewBox="0 0 24 24">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );

  // Get icon component based on tab ID
  const getIcon = (tabId) => {
    const isActive = activeTab === tabId;
    switch (tabId) {
      case "home":
        return <HomeIcon active={isActive} />;
      case "plan":
        return <PlanIcon active={isActive} />;
      case "users":
        return <UsersIcon active={isActive} />;
      case "profile":
        return <ProfileIcon active={isActive} />;
      case "create":
        return <PlusIcon />;
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <motion.div
        style={styles.footer}
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", damping: 25 }}
      >
        <div style={styles.tabContainer}>
          {/* Active tab highlight */}
          <motion.div
            style={styles.activeTabHighlight}
            layoutId="activeTab"
            initial={false}
            animate={{
              width: `${100 / tabs.length}%`,
              x: `${
                tabs.findIndex((tab) => tab.id === activeTab) *
                (100 / tabs.length)
              }%`,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />

          {/* Render tabs */}
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              style={styles.tabButton}
              onClick={() => handleTabClick(tab.id, tab.path)}
              whileTap={{ scale: 0.95 }}
            >
              {tab.id === "create" ? (
                <motion.div
                  style={styles.createButton}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <PlusIcon />
                </motion.div>
              ) : (
                <>
                  <motion.div
                    animate={{
                      scale: activeTab === tab.id ? 1.2 : 1,
                      y: activeTab === tab.id ? -5 : 0,
                    }}
                    transition={{ type: "spring", stiffness: 500 }}
                  >
                    {getIcon(tab.id)}
                  </motion.div>
                  <motion.span
                    style={{
                      ...styles.tabLabel,
                      color: activeTab === tab.id ? "#3B82F6" : "#6B7280",
                    }}
                    animate={{
                      opacity: activeTab === tab.id ? 1 : 0.7,
                      y: activeTab === tab.id ? 0 : 5,
                    }}
                  >
                    {tab.label}
                  </motion.span>
                </>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Footer;
